import React, { useState, useEffect, useRef } from 'react';
import {
  Calendar,
  Clock,
  CheckCircle,
  AlertTriangle,
  Sparkles,
  Brain,
  TrendingDown,
  BarChart,
  ArrowRight,
  Zap,
  Play,
  Target,
  Users,
  BookOpen
} from 'lucide-react';
import SectionControls from '../shared/SectionControls';

const SmartSchedulingCompact = ({ data, onAnimationComplete, onContinue, isLastSection = false }) => {
  const [isInView, setIsInView] = useState(false);
  const [animationStage, setAnimationStage] = useState(0);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [stressLevel, setStressLevel] = useState(100);
  const [timeSaved, setTimeSaved] = useState(0);
  const [groupedTasks, setGroupedTasks] = useState([]);
  const [showBefore, setShowBefore] = useState(true);
  const [animationsComplete, setAnimationsComplete] = useState(false);
  const [optimizationStep, setOptimizationStep] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isInView) {
          setIsInView(true);
          setTimeout(() => startAnimation(), 1000);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isInView]);

  const startAnimation = () => {
    // Reset states
    setAnimationStage(0);
    setIsOptimizing(false);
    setStressLevel(100);
    setTimeSaved(0);
    setGroupedTasks([]);
    setShowBefore(true);
    setAnimationsComplete(false);
    setOptimizationStep(0);

    // Stage 1: Show chaos (0-4000ms) - MUCH SLOWER to let users feel the overwhelm
    setTimeout(() => {
      setAnimationStage(1);
    }, 1000);

    // Stage 2: Start optimization (4500-7500ms) - LONGER optimization phase
    setTimeout(() => {
      setIsOptimizing(true);
      setAnimationStage(2);

      // Step 1: Analyzing tasks (at 2.5s)
      setOptimizationStep(1);

      // Step 2: Creating focus blocks (at 3.5s)
      setTimeout(() => setOptimizationStep(2), 1000);

      // Step 3: Optimizing energy (at 4.5s)
      setTimeout(() => setOptimizationStep(3), 2000);

      // Animate stress reduction more gradually
      let currentStress = 100;
      const stressInterval = setInterval(() => {
        currentStress -= 2; // Slower reduction
        if (currentStress <= 34) {
          setStressLevel(34);
          clearInterval(stressInterval);
        } else {
          setStressLevel(currentStress);
        }
      }, 80); // Slower interval

      // Animate time saved more gradually
      let currentTime = 0;
      const timeInterval = setInterval(() => {
        currentTime += 0.1; // Smaller increments
        if (currentTime >= 4.5) {
          setTimeSaved(4.5);
          clearInterval(timeInterval);
        } else {
          setTimeSaved(currentTime);
        }
      }, 80); // Slower interval
    }, 4500);

    // Stage 3: Show optimized result (7500ms)
    setTimeout(() => {
      setIsOptimizing(false);
      setAnimationStage(3);
      setShowBefore(false);
      setGroupedTasks([
        {
          title: 'Deep Work: Essay Writing',
          time: '2:00 PM - 4:30 PM',
          tasks: ['Common App Essay revision', 'MIT short answers draft', 'Stanford supplemental outline'],
          color: 'bg-emerald-100 border-emerald-300',
          icon: '✍️',
          benefit: 'Same mental mode = 2x faster'
        },
        {
          title: 'Admin Sprint',
          time: '4:45 PM - 5:30 PM',
          tasks: ['Request transcripts', 'Update counselor form', 'Send SAT scores'],
          color: 'bg-violet-100 border-violet-300',
          icon: '📄',
          benefit: 'Batch processing = Less stress'
        },
        {
          title: 'Research Block',
          time: '7:00 PM - 8:00 PM',
          tasks: ['USC program details', 'Northwestern deadline check'],
          color: 'bg-blue-100 border-blue-300',
          icon: '🔍',
          benefit: 'Focused research = Better fit'
        }
      ]);
    }, 7500);

    // Mark complete
    setTimeout(() => {
      setAnimationsComplete(true);
      onAnimationComplete && onAnimationComplete();
    }, 8500);
  };

  const replayAnimation = () => {
    startAnimation();
  };

  // Chaotic tasks for "before" view - more detailed
  const chaoticTasks = [
    { id: 1, title: 'Common App Essay - Final Draft', deadline: 'Due Tomorrow 11:59 PM', urgent: true, timeNeeded: '3 hrs' },
    { id: 2, title: 'MIT Short Answers (5)', deadline: 'Due in 2 days', urgent: true, timeNeeded: '2 hrs' },
    { id: 3, title: 'Request Transcript from School', deadline: 'Due in 3 days', urgent: false, timeNeeded: '15 min' },
    { id: 4, title: 'Stanford Essays - First Draft', deadline: 'Due in 5 days', urgent: false, timeNeeded: '4 hrs' },
    { id: 5, title: 'Update Counselor Form', deadline: 'Due Tomorrow', urgent: true, timeNeeded: '30 min' },
    { id: 6, title: 'Send SAT Scores to 8 Schools', deadline: 'Send Today', urgent: true, timeNeeded: '20 min' },
    { id: 7, title: 'USC Program Research', deadline: 'Due in 4 days', urgent: false, timeNeeded: '1 hr' },
    { id: 8, title: 'Northwestern Deadline Check', deadline: 'ASAP', urgent: false, timeNeeded: '10 min' }
  ];

  return (
    <div ref={sectionRef} className="max-w-7xl mx-auto px-4 w-full">
      {/* Compact Header */}
      <div className="text-center mb-6">
        <div className="flex items-center justify-center gap-3 mb-2">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 rounded-full">
            <Calendar className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-700">Smart Scheduling</span>
          </div>
        </div>
        <h2 className="text-3xl font-bold text-slate-900 mb-2">
          Never Miss Another Deadline
        </h2>
        <p className="text-lg text-slate-600 max-w-3xl mx-auto">
          AI analyzes your workload and creates optimal work sessions.
        </p>
      </div>

      {/* Main Content Area - Compact Layout */}
      <div className="relative bg-slate-50 rounded-xl p-6 min-h-[450px]">
        {/* Status Bar */}
        <div className="flex items-center justify-between mb-4 bg-white rounded-lg p-3 shadow-sm">
          <div className="flex items-center gap-6">
            {/* Stress Level */}
            <div className="flex items-center gap-2">
              <TrendingDown className={`w-4 h-4 ${stressLevel < 50 ? 'text-emerald-500' : 'text-red-500'}`} />
              <div>
                <div className="text-xs text-slate-600">Stress Level</div>
                <div className={`text-lg font-bold ${stressLevel < 50 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {stressLevel}%
                </div>
              </div>
            </div>

            {/* Time Saved */}
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-blue-500" />
              <div>
                <div className="text-xs text-slate-600">Time Saved</div>
                <div className="text-lg font-bold text-blue-600">
                  {timeSaved.toFixed(1)} hrs
                </div>
              </div>
            </div>

            {/* Toggle View */}
            {animationStage === 3 && (
              <button
                onClick={() => setShowBefore(!showBefore)}
                className="px-3 py-1 text-xs font-medium bg-slate-100 hover:bg-slate-200 rounded-full transition-colors"
              >
                View {showBefore ? 'After' : 'Before'}
              </button>
            )}
          </div>

          {/* Status Indicator */}
          <div className="flex items-center gap-2">
            {isOptimizing && (
              <>
                <Brain className="w-4 h-4 text-violet-600 animate-pulse" />
                <span className="text-sm text-violet-600 font-medium">Optimizing...</span>
              </>
            )}
            {animationStage === 3 && !isOptimizing && (
              <>
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span className="text-sm text-emerald-600 font-medium">Optimized</span>
              </>
            )}
          </div>
        </div>

        {/* Task View Area */}
        <div className="grid lg:grid-cols-2 gap-4">
          {/* Left: Task List */}
          <div>
            {(showBefore || animationStage < 3) ? (
              // Chaotic View
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-slate-700 mb-2">Your Deadlines (Chaos Mode)</h3>
                <div className="grid gap-2">
                  {chaoticTasks.slice(0, animationStage >= 1 ? 8 : 0).map((task, index) => (
                    <div
                      key={task.id}
                      className={`bg-white p-2.5 rounded-lg border-2 animate-fadeIn ${
                        task.urgent ? 'border-red-300 bg-red-50' : 'border-slate-200'
                      }`}
                      style={{ animationDelay: `${index * 250}ms` }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 flex-1">
                          {task.urgent && <AlertTriangle className="w-3 h-3 text-red-500 flex-shrink-0" />}
                          <span className="text-sm font-medium text-slate-700">{task.title}</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs">
                          <span className="text-slate-500">{task.timeNeeded}</span>
                          <span className={`${task.urgent ? 'text-red-600 font-bold' : 'text-slate-500'}`}>
                            {task.deadline}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              // Organized View
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-slate-700 mb-2">Today's Focus Sessions</h3>
                <div className="space-y-3">
                  {groupedTasks.map((group, index) => (
                    <div
                      key={index}
                      className={`p-3 rounded-lg border-2 ${group.color} animate-slideIn`}
                      style={{ animationDelay: `${index * 300}ms` }}
                    >
                      <div className="flex items-start gap-3">
                        <span className="text-2xl">{group.icon}</span>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-medium text-slate-900">{group.title}</h4>
                            <span className="text-xs font-medium text-slate-600">{group.time}</span>
                          </div>
                          <div className="space-y-1 mb-2">
                            {group.tasks.map((task, i) => (
                              <div key={i} className="flex items-center gap-1.5">
                                <CheckCircle className="w-3 h-3 text-emerald-500" />
                                <span className="text-xs text-slate-700">{task}</span>
                              </div>
                            ))}
                          </div>
                          <div className="text-xs font-medium text-emerald-700 bg-emerald-50/50 rounded px-2 py-1">
                            💡 {group.benefit}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: Workload Analysis */}
          <div className="space-y-4">
            {/* Workload Summary - Changes based on animation stage */}
            <div>
              <h3 className="text-sm font-medium text-slate-700 mb-2">
                {animationStage < 3 ? 'Current Workload' : 'Why This Works'}
              </h3>

              {animationStage < 3 ? (
                // Stress-inducing workload summary during chaos
                <div className="bg-white rounded-lg p-3 border-2 border-red-200 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-slate-900">Tasks Due Tomorrow</span>
                    <span className="text-lg font-bold text-red-600">3</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-slate-900">Total Hours Needed</span>
                    <span className="text-lg font-bold text-amber-600">10.5</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-slate-900">Hours Available Today</span>
                    <span className="text-lg font-bold text-slate-600">4</span>
                  </div>
                  <div className="border-t border-red-200 pt-2 mt-2">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-500" />
                      <span className="text-xs font-bold text-red-700">6.5 hours behind schedule</span>
                    </div>
                  </div>

                  {animationStage >= 1 && (
                    <div className="mt-3 space-y-1 animate-fadeIn">
                      <div className="text-xs text-slate-600">Coming up this week:</div>
                      <div className="flex justify-between text-xs">
                        <span>Wed: Stanford essays due</span>
                        <span className="font-medium">4 hrs</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span>Fri: USC research needed</span>
                        <span className="font-medium">1 hr</span>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                // Benefits explanation after optimization
                <div className="bg-white rounded-lg p-3 border border-slate-200 space-y-2">
                  <div className="flex items-start gap-2">
                    <Brain className="w-4 h-4 text-violet-500 mt-0.5" />
                    <div>
                      <div className="text-xs font-medium text-slate-900">Context Switching Eliminated</div>
                      <div className="text-xs text-slate-600">Similar tasks grouped together</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Target className="w-4 h-4 text-blue-500 mt-0.5" />
                    <div>
                      <div className="text-xs font-medium text-slate-900">Energy Optimization</div>
                      <div className="text-xs text-slate-600">Hard tasks when you're fresh</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Users className="w-4 h-4 text-emerald-500 mt-0.5" />
                    <div>
                      <div className="text-xs font-medium text-slate-900">Buffer Time Built In</div>
                      <div className="text-xs text-slate-600">Room for the unexpected</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Mini Calendar */}
            <div>
              <h3 className="text-sm font-medium text-slate-700 mb-2">This Week's Progress</h3>
              <div className="bg-white rounded-lg p-3 border border-slate-200">
                <div className="grid grid-cols-5 gap-2 text-xs">
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].map((day, index) => {
                    const dayNames = ['Monday', 'Tuesday', 'Today', 'Thursday', 'Friday'];
                    return (
                      <div key={day} className="text-center">
                        <div className="font-medium text-slate-600 mb-1">{day}</div>
                        <div className={`h-8 rounded flex items-center justify-center relative ${
                          animationStage === 3 && !showBefore && index < 3
                            ? 'bg-emerald-100 text-emerald-700 font-bold'
                            : animationStage >= 1 && (index === 0 || index === 1)
                            ? 'bg-red-100 text-red-600'
                            : 'bg-slate-50'
                        }`}>
                          {animationStage >= 1 && index === 0 && '5'}
                          {animationStage >= 1 && index === 1 && '4'}
                          {animationStage === 3 && !showBefore && index === 0 && '✓'}
                          {animationStage === 3 && !showBefore && index === 1 && '✓'}
                          {animationStage === 3 && !showBefore && index === 2 && '3'}
                          {index === 2 && animationStage === 3 && !showBefore && (
                            <span className="absolute -top-5 text-xs bg-blue-600 text-white px-1 rounded">Today</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Quick Stats */}
                {animationStage === 3 && !showBefore && (
                  <div className="mt-3 pt-3 border-t border-slate-200 grid grid-cols-3 gap-2 text-xs">
                    <div className="text-center">
                      <div className="font-bold text-emerald-600">9</div>
                      <div className="text-slate-500">Completed</div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-blue-600">3</div>
                      <div className="text-slate-500">Today</div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-amber-600">0</div>
                      <div className="text-slate-500">Overdue</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Processing Overlay */}
        {isOptimizing && (
          <div className="absolute inset-0 bg-white/95 backdrop-blur-sm rounded-xl flex items-center justify-center z-10">
            <div className="text-center max-w-md">
              <div className="w-20 h-20 bg-gradient-to-br from-violet-100 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Brain className="w-10 h-10 text-violet-600 animate-pulse" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-4">
                AI Optimizing Your Schedule
              </h3>

              {/* Progress Steps */}
              <div className="space-y-3 text-left mb-6">
                <div className={`flex items-center gap-3 transition-all duration-500 ${
                  optimizationStep >= 1 ? 'opacity-100' : 'opacity-40'
                }`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                    optimizationStep >= 2 ? 'bg-emerald-500' : optimizationStep >= 1 ? 'bg-blue-500' : 'bg-slate-300'
                  }`}>
                    {optimizationStep >= 2 ? (
                      <CheckCircle className="w-4 h-4 text-white" />
                    ) : optimizationStep >= 1 ? (
                      <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                    ) : (
                      <span className="w-2 h-2 bg-white rounded-full" />
                    )}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-slate-900">Analyzing 8 tasks</div>
                    <div className="text-xs text-slate-600">Identifying dependencies and priorities</div>
                  </div>
                </div>

                <div className={`flex items-center gap-3 transition-all duration-500 ${
                  optimizationStep >= 2 ? 'opacity-100' : 'opacity-40'
                }`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                    optimizationStep >= 3 ? 'bg-emerald-500' : optimizationStep >= 2 ? 'bg-blue-500' : 'bg-slate-300'
                  }`}>
                    {optimizationStep >= 3 ? (
                      <CheckCircle className="w-4 h-4 text-white" />
                    ) : optimizationStep >= 2 ? (
                      <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                    ) : (
                      <span className="w-2 h-2 bg-white rounded-full" />
                    )}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-slate-900">Creating focus blocks</div>
                    <div className="text-xs text-slate-600">Grouping similar tasks together</div>
                  </div>
                </div>

                <div className={`flex items-center gap-3 transition-all duration-500 ${
                  optimizationStep >= 3 ? 'opacity-100' : 'opacity-40'
                }`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                    optimizationStep >= 3 ? 'bg-blue-500' : 'bg-slate-300'
                  }`}>
                    {optimizationStep >= 3 ? (
                      <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                    ) : (
                      <span className="w-2 h-2 bg-white rounded-full" />
                    )}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-slate-900">Optimizing energy levels</div>
                    <div className="text-xs text-slate-600">Matching task difficulty to your peak hours</div>
                  </div>
                </div>
              </div>

              {/* Real-time metrics */}
              <div className="flex justify-center gap-8">
                <div>
                  <div className="text-2xl font-bold text-red-600 animate-pulse">
                    {stressLevel}%
                  </div>
                  <div className="text-xs text-slate-600">Stress Level</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-emerald-600 animate-pulse">
                    {timeSaved.toFixed(1)} hrs
                  </div>
                  <div className="text-xs text-slate-600">Time Saved</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Section Controls */}
      <SectionControls
        onReplay={replayAnimation}
        onContinue={onContinue}
        isLastSection={isLastSection}
        showControls={animationsComplete}
      />

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
          opacity: 0;
        }

        .animate-slideIn {
          animation: slideIn 0.4s ease-out forwards;
          opacity: 0;
        }
      `}</style>
    </div>
  );
};

export default SmartSchedulingCompact;